<template>
  <div class="project-internal">
    <AccessBlock :id="PROJECT_BASE_INFORMATION_BLOCK">
      <div class="accordion" role="tablist">
        <div class="mb-3">
          <b-button
            block
            v-b-toggle.project-internal-base-information
            variant="outline-primary"
            class="text-left accordion-header"
          >
            <i class="fa fa-info-circle mr-2"></i>{{ $t('project_base_information') }}
            <i class="fa fa-chevron-down float-right mt-1"></i>
          </b-button>
          <b-collapse id="project-internal-base-information" role="tabpanel">
            <div class="accordion-content px-2 py-2">
              <ProjectBaseInformation
                v-if="baseInformationProject"
                :blockId="PROJECT_BASE_INFORMATION_BLOCK"
                :project="baseInformationProject"
                :organization="project.client"
              />
            </div>
          </b-collapse>
        </div>
      </div>
    </AccessBlock>

    <AccessBlock :id="PROJECT_PRESENTATIONS_BLOCK">
      <div class="accordion" role="tablist">
        <div class="mb-3">
          <b-button
            block
            v-b-toggle.project-internal-presentations
            variant="outline-primary"
            class="text-left accordion-header"
          >
            <i class="fa fa-file-pdf-o mr-2"></i>{{ $t('project_presentations') }}
            <i class="fa fa-chevron-down float-right mt-1"></i>
          </b-button>
          <b-collapse id="project-internal-presentations" role="tabpanel">
            <div class="accordion-content px-2 py-2">
              <ProjectPresentations v-if="presentationsProject" :project="presentationsProject" />
            </div>
          </b-collapse>
        </div>
      </div>
    </AccessBlock>

    <AccessBlock :id="PROJECT_CLARIFICATION_BLOCK">
      <div class="accordion" role="tablist">
        <div class="mb-3">
          <b-button
            block
            v-b-toggle.project-internal-deferred-state
            variant="outline-primary"
            class="text-left accordion-header"
          >
            <i class="fa fa-clock-o mr-2"></i>{{ $t('deferredState') }}
            <i class="fa fa-chevron-down float-right mt-1"></i>
          </b-button>
          <b-collapse id="project-internal-deferred-state" role="tabpanel">
            <div class="accordion-content px-2 py-2">
              <ProjectClarification
                v-if="clarificationProject"
                :blockId="PROJECT_CLARIFICATION_BLOCK"
                :project="clarificationProject"
              />
            </div>
          </b-collapse>
        </div>
      </div>
    </AccessBlock>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { getDocumentStateColor } from '@/helpers/colors';
import InlineEditor from '@ckeditor/ckeditor5-build-inline';
import AccessBlock from '@/components/Access/AccessBlock.vue';
import ProjectBaseInformation from '@/blocks/project/ProjectBaseInformation.vue';
import ProjectPresentations from '@/blocks/project/ProjectPresentations.vue';
import ProjectClarification from '@/blocks/project/ProjectClarification.vue';
import {
  PROJECT_BASE_INFORMATION_BLOCK,
  PROJECT_PRESENTATIONS_BLOCK,
  PROJECT_CLARIFICATION_BLOCK,
} from '@/navigation/accessNodes';

/** Number of the unsaved project (ProjectEdit `_new`); it has no blocks yet. */
const NEW_PROJECT_NUMBER = '_new';

export default {
  components: {
    AccessBlock,
    ProjectBaseInformation,
    ProjectPresentations,
    ProjectClarification,
  },
  props: {
    project: { type: Object },
  },
  data() {
    return {
      PROJECT_BASE_INFORMATION_BLOCK,
      PROJECT_PRESENTATIONS_BLOCK,
      PROJECT_CLARIFICATION_BLOCK,
      editor: InlineEditor,
      editorConfig: {},
    };
  },
  computed: {
    /**
     * Data of the block project-base-information: the excerpt from `Project.blocks`, null when
     * not loaded or denied. The unsaved project has no blocks; the block edits the project
     * itself and its values go with `createProject` (plan
     * docs/work/_done/2026-10-project-block-base-information, decided 7).
     */
    baseInformationProject() {
      if (this.project.number === NEW_PROJECT_NUMBER) {
        return this.project;
      }
      return this.project.blocks ? this.project.blocks.baseInformation || null : null;
    },
    /**
     * Data of the block project-presentations: the excerpt from `Project.blocks`, null when not
     * loaded or denied. The unsaved project has no blocks; the block gets the project itself
     * only to show its hint (plan docs/work/_done/2026-10-project-block-presentations).
     */
    presentationsProject() {
      if (this.project.number === NEW_PROJECT_NUMBER) {
        return this.project;
      }
      return this.project.blocks?.presentations ?? null;
    },
    /**
     * Data of the block project-clarification: the excerpt from `Project.blocks`, null when not
     * loaded or denied. The unsaved project has no blocks; the block edits the project itself and
     * `deferredState` goes with `createProject`
     * (plan docs/work/_done/2026-10-project-block-clarification).
     */
    clarificationProject() {
      if (this.project.number === NEW_PROJECT_NUMBER) {
        return this.project;
      }
      return this.project.blocks?.clarification ?? null;
    },

    /**
     * @todo DRY
     */
    invalidFeedbackNA() {
      return this.$t('preconditionNA');
    },
  },
  methods: {
    ...mapActions(['fetchEnumValues']),
    documentStateColor(state) {
      return getDocumentStateColor(state);
    },

    async setup() {
      await this.fetchEnumValues('AskEmployerCategoryState');
      await this.fetchEnumValues('DocumentState');
    },
    /**
     * check requirementsValidState to show mark input fields
     * @todo DRY
     */
    requirementsValidState(field) {
      if (!this.project.netRegistrationRequirements) return false;

      const requirement = this.project.netRegistrationRequirements.find(
        (requirement) => requirement.name === field
      );
      return requirement && requirement.fullfilledState === 'YES' ? true : false;
    },
  },

  async mounted() {
    await this.setup();
  },
};
</script>

<style scoped lang="scss">
.form-grid-container-left {
  display: grid;
  grid-template-columns: 1fr 2fr;
  column-gap: 1em;
  row-gap: 1em;
}

.form-grid-container-left .label {
  text-align: right;
}

.dense-editor {
  height: auto;
  max-height: 300px;
  overflow-y: auto;
}

.ck-editor__editable_inline {
  min-height: 110px;
  height: auto;
  max-height: 300px;
  overflow-y: auto;
}

::v-deep .multiselect__tags {
  border: 0;
  border-radius: 0;
}
</style>
